import { EncodingOperation, EncoderToolType } from '../types';

export interface Base64ImageConfiguration {
  id: string;
  operation: 'encode' | 'decode';
  inputFile?: File;
  inputBase64?: string;
  outputFormat: 'png' | 'jpg' | 'jpeg' | 'webp' | 'gif';
  quality?: number; // 0-1 for lossy formats
  maxWidth?: number;
  maxHeight?: number;
  preserveAspectRatio: boolean;
  processedAt: Date;
}

export interface ImageMetadata {
  width: number;
  height: number;
  format: string;
  size: number; // in bytes
  colorSpace?: string;
  hasAlpha: boolean;
  orientation?: number;
}

export class Base64ImageService {
  private static readonly DEFAULT_CONFIG: Partial<Base64ImageConfiguration> = {
    outputFormat: 'png',
    quality: 0.9,
    preserveAspectRatio: true,
    maxWidth: 2048,
    maxHeight: 2048
  };

  private static readonly SUPPORTED_FORMATS = ['png', 'jpg', 'jpeg', 'webp', 'gif'];
  private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  static createConfiguration(
    operation: 'encode' | 'decode',
    options: Partial<Base64ImageConfiguration> = {}
  ): Base64ImageConfiguration {
    return {
      id: `base64img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      operation,
      ...this.DEFAULT_CONFIG,
      ...options,
      processedAt: new Date()
    } as Base64ImageConfiguration;
  }

  static async encode(config: Base64ImageConfiguration): Promise<EncodingOperation> {
    try {
      if (!config.inputFile) {
        throw new Error('No file provided for encoding');
      }

      // Validate file
      this.validateFile(config.inputFile);

      // Get image metadata
      const metadata = await this.getImageMetadata(config.inputFile);

      // Process image if needed
      const processedFile = await this.processImage(config.inputFile, config);

      // Convert to base64
      const base64Data = await this.fileToBase64(processedFile);

      // Get final metadata
      const finalMetadata = await this.getImageMetadata(processedFile);

      return {
        id: config.id,
        toolType: EncoderToolType.BASE64_IMAGE,
        operationType: 'encode',
        inputData: config.inputFile,
        outputData: base64Data,
        timestamp: config.processedAt,
        status: 'success',
        metadata: {
          originalMetadata: metadata,
          finalMetadata,
          outputFormat: config.outputFormat,
          quality: config.quality
        }
      };
    } catch (error) {
      return {
        id: config.id,
        toolType: EncoderToolType.BASE64_IMAGE,
        operationType: 'encode',
        inputData: config.inputFile || '',
        timestamp: config.processedAt,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Encoding failed'
      };
    }
  }

  static async decode(config: Base64ImageConfiguration): Promise<EncodingOperation> {
    try {
      if (!config.inputBase64) {
        throw new Error('No base64 data provided for decoding');
      }

      // Validate base64 data
      this.validateBase64Data(config.inputBase64);

      // Convert base64 to blob
      const blob = this.base64ToBlob(config.inputBase64);

      // Create file from blob
      const file = new File([blob], 'decoded-image', { type: blob.type });

      // Get image metadata
      const metadata = await this.getImageMetadata(file);

      return {
        id: config.id,
        toolType: EncoderToolType.BASE64_IMAGE,
        operationType: 'decode',
        inputData: config.inputBase64,
        outputData: file,
        timestamp: config.processedAt,
        status: 'success',
        metadata: {
          imageMetadata: metadata,
          originalFormat: blob.type
        }
      };
    } catch (error) {
      return {
        id: config.id,
        toolType: EncoderToolType.BASE64_IMAGE,
        operationType: 'decode',
        inputData: config.inputBase64 || '',
        timestamp: config.processedAt,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Decoding failed'
      };
    }
  }

  static async getImageMetadata(file: File): Promise<ImageMetadata> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Check for alpha channel
        const imageData = ctx.getImageData(0, 0, 1, 1);
        const hasAlpha = imageData.data.length === 4;

        resolve({
          width: img.width,
          height: img.height,
          format: file.type.split('/')[1] || 'unknown',
          size: file.size,
          hasAlpha,
          colorSpace: 'sRGB' // Default assumption
        });
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for metadata extraction'));
      };

      img.src = URL.createObjectURL(file);
    });
  }

  static validateFile(file: File): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!file) {
      errors.push('No file provided');
      return { isValid: false, errors };
    }

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      errors.push(`File size exceeds maximum limit (${this.MAX_FILE_SIZE / 1024 / 1024}MB)`);
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      errors.push('File must be an image');
    }

    // Check supported formats
    const format = file.type.split('/')[1];
    if (format && !this.SUPPORTED_FORMATS.includes(format.toLowerCase())) {
      errors.push(`Unsupported format: ${format}. Supported formats: ${this.SUPPORTED_FORMATS.join(', ')}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static validateBase64Data(base64Data: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!base64Data || base64Data.trim().length === 0) {
      errors.push('Base64 data cannot be empty');
      return { isValid: false, errors };
    }

    // Check base64 format
    try {
      // Check if it has data URL prefix
      if (base64Data.startsWith('data:')) {
        const matches = base64Data.match(/^data:(image\/[^;]+);base64,(.+)$/);
        if (!matches) {
          errors.push('Invalid data URL format');
        } else {
          // Validate the base64 part
          const base64Content = matches[2];
          this.validateBase64Content(base64Content, errors);
        }
      } else {
        // Validate as plain base64
        this.validateBase64Content(base64Data, errors);
      }
    } catch (error) {
      errors.push('Invalid base64 format');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private static validateBase64Content(content: string, errors: string[]): void {
    // Check base64 character set
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(content)) {
      errors.push('Invalid base64 characters');
    }

    // Check padding
    const padding = content.length % 4;
    if (padding !== 0) {
      errors.push('Invalid base64 padding');
    }

    // Check for reasonable size
    const estimatedSize = Math.ceil(content.length * 0.75); // Base64 is ~33% larger
    if (estimatedSize > this.MAX_FILE_SIZE) {
      errors.push(`Decoded image would exceed maximum size limit`);
    }
  }

  private static async processImage(file: File, config: Base64ImageConfiguration): Promise<File> {
    // If no processing is needed, return original file
    if (!config.maxWidth && !config.maxHeight && file.type.includes(`image/${config.outputFormat}`)) {
      return file;
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculate new dimensions
        let { width, height } = this.calculateDimensions(
          img.width, 
          img.height, 
          config.maxWidth, 
          config.maxHeight, 
          config.preserveAspectRatio
        );

        canvas.width = width;
        canvas.height = height;

        // Draw image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with desired format
        const mimeType = `image/${config.outputFormat}`;
        const quality = config.quality || 0.9;

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to process image'));
            return;
          }

          const processedFile = new File([blob], file.name, { type: mimeType });
          resolve(processedFile);
        }, mimeType, quality);
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for processing'));
      };

      img.src = URL.createObjectURL(file);
    });
  }

  private static calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth?: number,
    maxHeight?: number,
    preserveAspectRatio: boolean = true
  ): { width: number; height: number } {
    if (!maxWidth && !maxHeight) {
      return { width: originalWidth, height: originalHeight };
    }

    if (!preserveAspectRatio) {
      return {
        width: maxWidth || originalWidth,
        height: maxHeight || originalHeight
      };
    }

    const aspectRatio = originalWidth / originalHeight;
    let width = originalWidth;
    let height = originalHeight;

    // Apply width constraint
    if (maxWidth && width > maxWidth) {
      width = maxWidth;
      height = width / aspectRatio;
    }

    // Apply height constraint
    if (maxHeight && height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }

    return { width, height };
  }

  private static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  private static base64ToBlob(base64Data: string): Blob {
    try {
      // Handle data URL format
      if (base64Data.startsWith('data:')) {
        const matches = base64Data.match(/^data:(image\/[^;]+);base64,(.+)$/);
        if (!matches) {
          throw new Error('Invalid data URL format');
        }

        const mimeType = matches[1];
        const base64Content = matches[2];
        const byteCharacters = atob(base64Content);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
      } else {
        // Handle plain base64
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: 'image/png' }); // Default to PNG
      }
    } catch (error) {
      throw new Error('Failed to convert base64 to blob');
    }
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}